import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import { pageLinks } from '../../pageLinks'
import { AppShell } from '../../components/AppShell'
import { apiBase } from 'txom-frontend-libs'
import { getEnabledMarketplaces, type MarketplacePreference } from './aplusMarketplaceStore'
import { saveScreenshot, loadAllScreenshots, formatCapturedAt } from './aplusScreenshotCache'

// ============================================================================
// Data & Types
// ============================================================================

type CaptureStatus = 'idle' | 'loading' | 'done' | 'error'

interface CountryCapture {
  code: string
  status: CaptureStatus
  imageUrl?: string // object URL or data URL
  capturedAt?: string // ISO date string
  error?: string
}

// ============================================================================
// Components
// ============================================================================

function StatusBadge({ status }: { status: CaptureStatus }) {
  if (status === 'done') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F0FDF4] text-[#16A34A] text-[11px] font-medium whitespace-nowrap">
        <span className="size-1.5 rounded-full bg-[#16A34A]" />
        Captured
      </span>
    )
  }
  if (status === 'loading') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFFBEB] text-[#F59E0B] text-[11px] font-medium whitespace-nowrap">
        <span className="size-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
        Capturing...
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#EF4444] text-[11px] font-medium whitespace-nowrap">
        <span className="size-1.5 rounded-full bg-[#EF4444]" />
        Error
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[#71717A] text-[11px] font-medium whitespace-nowrap">
      <span className="size-1.5 rounded-full bg-[#A1A1AA]" />
      Pending
    </span>
  )
}

// Single country column
function CountryColumn({
  capture,
  marketplace,
  asin,
  onCapture,
  quotaExhausted,
}: {
  capture: CountryCapture
  marketplace: MarketplacePreference
  asin: string
  onCapture: () => void
  quotaExhausted: boolean
}) {
  const mp = marketplace
  const amazonUrl = `https://www.${mp.domain}/dp/${asin}`

  return (
    <div className="shrink-0 w-[320px] flex flex-col bg-white rounded-lg border border-[#E5E5E5] overflow-hidden">
      {/* Column header */}
      <div className="px-3 py-2.5 border-b border-[#E5E5E5] bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className="text-base leading-none">{mp.flag}</span>
            <span className="text-[13px] font-semibold text-[#18181B]">{mp.name}</span>
          </div>
          <div className="flex items-center gap-1">
            <a
              href={amazonUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1 rounded hover:bg-[#F5F5F5] transition-colors"
              title={`Open on ${mp.domain}`}
            >
              <svg className="size-3.5 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
            <button
              onClick={onCapture}
              disabled={capture.status === 'loading' || quotaExhausted}
              className="p-1 rounded hover:bg-[#F5F5F5] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title={quotaExhausted ? 'Daily screenshot limit reached' : 'Capture screenshot'}
            >
              <svg className={`size-3.5 text-[#A1A1AA] ${capture.status === 'loading' ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={capture.status} />
          {capture.status === 'done' && capture.capturedAt && (
            <span className="text-[10px] text-[#A1A1AA]">{formatCapturedAt(capture.capturedAt)}</span>
          )}
        </div>
      </div>

      {/* Screenshot content */}
      {capture.status === 'done' && capture.imageUrl ? (
        <div className="bg-[#FAFAFA] flex-1 overflow-hidden">
          <img
            src={capture.imageUrl}
            alt={`${mp.name} A+ content`}
            className="w-full h-auto"
          />
        </div>
      ) : capture.status === 'loading' ? (
        <div className="bg-[#FAFAFA] flex-1 flex items-center justify-center min-h-[400px]">
          <div className="text-center px-4">
            <div className="size-10 mx-auto mb-3 rounded-full bg-[#FFFBEB] flex items-center justify-center">
              <svg className="size-5 text-[#F59E0B] animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </div>
            <p className="text-[12px] font-medium text-[#F59E0B] mb-0.5">Capturing screenshot...</p>
            <p className="text-[11px] text-[#A1A1AA]">This may take 10-20 seconds</p>
          </div>
        </div>
      ) : capture.status === 'error' ? (
        <div className="bg-[#FAFAFA] flex-1 flex items-center justify-center min-h-[300px]">
          <div className="text-center px-4">
            <div className="size-10 mx-auto mb-2 rounded-full bg-[#FEF2F2] flex items-center justify-center">
              <svg className="size-5 text-[#EF4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <p className="text-[12px] font-medium text-[#EF4444] mb-0.5">Capture failed</p>
            <p className="text-[11px] text-[#A1A1AA] mb-2 max-w-[280px] break-all">{capture.error || 'Unknown error'}</p>
            <button
              onClick={onCapture}
              disabled={quotaExhausted}
              className="px-3 h-7 bg-[#18181B] text-white rounded-md text-[11px] font-medium hover:bg-[#3F3F46] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Retry
            </button>
          </div>
        </div>
      ) : (
        /* idle — show capture button */
        <div className="bg-[#FAFAFA] flex-1 flex items-center justify-center min-h-[300px]">
          <div className="text-center px-4">
            <div className="size-10 mx-auto mb-2 rounded-full bg-[#F5F5F5] flex items-center justify-center">
              <svg className="size-5 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
              </svg>
            </div>
            <p className="text-[12px] text-[#71717A] mb-2">No screenshot yet</p>
            <button
              onClick={onCapture}
              disabled={quotaExhausted}
              className="px-3 h-7 bg-[#18181B] text-white rounded-md text-[11px] font-medium hover:bg-[#3F3F46] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {quotaExhausted ? 'Limit Reached' : 'Capture'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Daily screenshot quota (50/day, resets after 24h)
// ============================================================================

const DAILY_LIMIT = 50
const QUOTA_KEY = 'aplus_screenshot_quota'

function getQuota(): { used: number; resetsAt: string } {
  try {
    const raw = localStorage.getItem(QUOTA_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (new Date(parsed.resetsAt) > new Date()) return parsed
    }
  } catch { /* corrupted — reset */ }
  return { used: 0, resetsAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }
}

function consumeQuota(): boolean {
  const quota = getQuota()
  if (quota.used >= DAILY_LIMIT) return false
  quota.used += 1
  localStorage.setItem(QUOTA_KEY, JSON.stringify(quota))
  return true
}

function formatTimeUntilReset(resetsAt: string): string {
  const diff = new Date(resetsAt).getTime() - Date.now()
  if (diff <= 0) return 'now'
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

// ============================================================================
// Screenshot capture hook
// ============================================================================

function useScreenshotCapture(asin: string, marketplaces: MarketplacePreference[]) {
  const [captures, setCaptures] = useState<CountryCapture[]>(
    marketplaces.map((mp) => ({ code: mp.code, status: 'idle' }))
  )
  const [capturingAll, setCapturingAll] = useState(false)
  const [currentBatchLabel, setCurrentBatchLabel] = useState('')
  const [quota, setQuota] = useState(getQuota)

  const refreshQuota = () => setQuota(getQuota())

  // Load cached screenshots from IndexedDB on mount
  useEffect(() => {
    loadAllScreenshots(asin).then((cached) => {
      if (cached.length === 0) return
      setCaptures((prev) =>
        prev.map((c) => {
          const hit = cached.find((s) => s.marketplace === c.code)
          if (hit) {
            return { ...c, status: 'done', imageUrl: hit.dataUrl, capturedAt: hit.capturedAt }
          }
          return c
        })
      )
    }).catch(() => {
      // IndexedDB unavailable — continue without cache
    })
  }, [asin])

  const updateCapture = (code: string, update: Partial<CountryCapture>) => {
    setCaptures((prev) =>
      prev.map((c) => (c.code === code ? { ...c, ...update } : c))
    )
  }

  const captureOne = async (marketplace: string) => {
    if (!consumeQuota()) {
      refreshQuota()
      updateCapture(marketplace, { status: 'error', error: `Daily limit reached (${DAILY_LIMIT}/day). Resets in ${formatTimeUntilReset(quota.resetsAt)}.` })
      return
    }
    refreshQuota()
    updateCapture(marketplace, { status: 'loading', error: undefined, capturedAt: undefined })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 45000) // 45s timeout

    try {
      const url = apiBase + '/screenshot/capture'
      console.log(`[screenshot] Calling ${url} for ${marketplace}`)

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asin, marketplace }),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!res.ok) {
        const contentType = res.headers.get('content-type') || ''
        let errorMsg = `HTTP ${res.status}`
        if (contentType.includes('application/json')) {
          const err = await res.json().catch(() => ({}))
          errorMsg = err.error || err.message || errorMsg
        } else {
          const text = await res.text().catch(() => '')
          errorMsg = text.substring(0, 100) || errorMsg
        }
        throw new Error(errorMsg)
      }

      const blob = await res.blob()

      // Save to IndexedDB for persistence
      const cached = await saveScreenshot(asin, marketplace, blob).catch(() => null)
      const imageUrl = cached ? cached.dataUrl : URL.createObjectURL(blob)
      const capturedAt = cached ? cached.capturedAt : new Date().toISOString()

      updateCapture(marketplace, { status: 'done', imageUrl, capturedAt })
    } catch (err: any) {
      clearTimeout(timeoutId)
      const msg = err.name === 'AbortError' ? 'Request timed out (45s)' : err.message
      console.error(`[screenshot] ${marketplace} error:`, msg)
      updateCapture(marketplace, { status: 'error', error: msg })
    }
  }

  const captureAll = async () => {
    setCapturingAll(true)
    // Capture all in batches of 2 to avoid overloading
    for (let i = 0; i < marketplaces.length; i += 2) {
      const batch = marketplaces.slice(i, i + 2)
      const batchNames = batch.map((mp) => mp.name).join(', ')
      setCurrentBatchLabel(`Capturing ${batchNames}... (${i + 1}–${Math.min(i + 2, marketplaces.length)} of ${marketplaces.length})`)
      await Promise.all(batch.map((mp) => captureOne(mp.code)))
    }
    setCapturingAll(false)
    setCurrentBatchLabel('')
  }

  return { captures, captureOne, captureAll, capturingAll, currentBatchLabel, quota, remaining: DAILY_LIMIT - quota.used }
}

// ============================================================================
// Page
// ============================================================================

export default function AplusListingDetail() {
  const { id } = useParams<{ id: string }>()
  const asin = id || 'B0CX23V2ZK' // Use route param or fallback
  const location = useLocation()
  const fromAmazonListings = (location.state as any)?.from === 'amazon-listings'

  const marketplaces = getEnabledMarketplaces()
  const { captures, captureOne, captureAll, capturingAll, currentBatchLabel, quota, remaining } = useScreenshotCapture(asin, marketplaces)

  const doneCount = captures.filter((c) => c.status === 'done').length
  const loadingCount = captures.filter((c) => c.status === 'loading').length
  const errorCount = captures.filter((c) => c.status === 'error').length
  const idleCount = captures.filter((c) => c.status === 'idle').length
  const isCapturing = loadingCount > 0

  return (
    <AppShell activeNav="listings">
      <div className="flex-1 flex flex-col min-h-0">
        {/* Top bar */}
        <div className="shrink-0 px-5 pt-4 pb-3 border-b border-[#E5E5E5] bg-white">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[12px] text-[#A1A1AA] mb-2">
            {fromAmazonListings ? (
              <>
                <Link to="/amazon/listings" className="hover:text-[#52525B] transition-colors">Amazon Listings</Link>
                <span>/</span>
                <span className="text-[#52525B]">{asin}</span>
              </>
            ) : (
              <>
                <Link to={pageLinks.AplusListings()} className="hover:text-[#52525B] transition-colors">Listings</Link>
                <span>/</span>
                <span className="text-[#52525B]">{asin}</span>
              </>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-base font-semibold text-[#18181B]">{asin}</h1>
                <p className="text-[12px] text-[#71717A]">Amazon Listing A+ Content</p>
              </div>
              {/* Summary stats */}
              <div className="flex items-center gap-3 ml-2">
                {doneCount > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className="size-1.5 rounded-full bg-[#16A34A]" />
                    <span className="text-[#52525B]">{doneCount} captured</span>
                  </div>
                )}
                {loadingCount > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className="size-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                    <span className="text-[#52525B]">{loadingCount} capturing</span>
                  </div>
                )}
                {errorCount > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className="size-1.5 rounded-full bg-[#EF4444]" />
                    <span className="text-[#52525B]">{errorCount} failed</span>
                  </div>
                )}
                {idleCount > 0 && doneCount + loadingCount + errorCount > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className="size-1.5 rounded-full bg-[#A1A1AA]" />
                    <span className="text-[#52525B]">{idleCount} pending</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={captureAll}
              disabled={isCapturing || remaining <= 0}
              className="px-3 h-8 bg-[#18181B] text-white rounded-lg text-[12px] font-medium flex items-center gap-1.5 hover:bg-[#3F3F46] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCapturing ? (
                <svg className="size-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              ) : (
                <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
                </svg>
              )}
              {isCapturing ? 'Capturing...' : 'Capture All'}
            </button>
          </div>
        </div>

        {/* Daily quota banner */}
        <div className="shrink-0 px-5 py-2 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-between">
          <p className="text-[12px] text-[#71717A]">
            Preview screenshots: <span className={`font-medium ${remaining <= 5 ? 'text-[#EF4444]' : 'text-[#18181B]'}`}>{remaining}</span>/{DAILY_LIMIT} remaining today
          </p>
          {remaining <= 0 && (
            <p className="text-[11px] text-[#EF4444] font-medium">
              Resets in {formatTimeUntilReset(quota.resetsAt)}
            </p>
          )}
        </div>

        {/* Capture All progress banner */}
        {capturingAll && (
          <div className="shrink-0 px-5 py-2.5 bg-[#FFFBEB] border-b border-[#FDE68A] flex items-center gap-3">
            <div className="size-4 rounded-full border-2 border-[#F59E0B] border-t-transparent animate-spin shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-[#92400E]">
                Sequencing screenshots across {marketplaces.length} marketplaces
              </p>
              <p className="text-[11px] text-[#B45309] mt-0.5">{currentBatchLabel}</p>
            </div>
            <div className="shrink-0 flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-[#92400E]">{doneCount}/{marketplaces.length}</span>
              <div className="w-20 h-1.5 bg-[#FDE68A] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#F59E0B] rounded-full transition-all duration-500"
                  style={{ width: `${(doneCount / marketplaces.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Horizontal scroll area */}
        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
          <div className="inline-flex gap-3 items-start p-4">
            {captures.map((capture) => {
              const mp = marketplaces.find((m) => m.code === capture.code)
              if (!mp) return null
              return (
                <CountryColumn
                  key={capture.code}
                  capture={capture}
                  marketplace={mp}
                  asin={asin}
                  onCapture={() => captureOne(capture.code)}
                  quotaExhausted={remaining <= 0}
                />
              )
            })}
          </div>
        </div>
      </div>
    </AppShell>
  )
}
